import styles from "./edit-client.module.css";

import { inject, observer } from "mobx-react";
import { WarningSign } from "../../../../components/svg";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { Ref, useEffect, useRef, useState } from "react";
import { ClientDataStore } from "../../store/store";
import ResidentNameField from "./components/client-name-input";
import { scrollIntoViewAndAlign } from "../../../../utils/scroll";
import CancelButton from "../../../../components/buttons/CancelButton";
import SaveButton from "../../../../components/buttons/SaveButton";
import { runInAction } from "mobx";
import LocationSelector, {
    LocationItem,
} from "../../../../components/location-selector/LocationSelector";
import LocationSelectorStore from "../../../../store/location-selector-store";
import { TreeNodeItem } from "../../../../components/tree-select/TreeSelect";

interface EditClientProps {
    clientDataStore?: ClientDataStore;
    locationSelectorStore?: LocationSelectorStore;
    saveLocked?: boolean;
    onSaveClick(): void;
    onCancelClick(): void;
}

function EditClient(props: EditClientProps) {
    const editComponentElement: Ref<any> = useRef(null);
    const clientDataStore = props.clientDataStore!;
    const locationSelectorStore = props.locationSelectorStore!;
    const showAddNewTitle = !clientDataStore.isNewClient;
    const { saveLocked, onSaveClick, onCancelClick } = props;
    const { t } = useTranslation("client-grid");
    const { t: commonT } = useTranslation("common");
    const occupiedLocationIds = clientDataStore.occupiedLocations;
    const selectedClient = clientDataStore.selectedClient;

    useEffect(() => {
        scrollIntoViewAndAlign(editComponentElement.current);

        return function unmount() {
            runInAction(() => {
                clientDataStore.errors = null;
                clientDataStore.errorMessages = null;
                clientDataStore.setValidated(true);
            });
        };
    }, []);

    const getLocationList = () => {
        const locationItemList: LocationItem[] =
            locationSelectorStore.selectedRoomList
                .map((room: TreeNodeItem) => {
                    return {
                        locationId: room.id,
                        locationName: room.name,
                        areaName: room.path || "",
                    };
                })
                .filter((location) => {
                    const locationId = Number(location.locationId);

                    return (
                        !occupiedLocationIds.includes(locationId) ||
                        locationId === Number(selectedClient.locationId!)
                    );
                });
        return locationItemList;
    };

    const setSelectedClientLocation = (value: LocationItem) => {
        if (value === null) {
            clientDataStore!.setSelectedClientLocationData(0, "", "");
            clientDataStore!.validateSelectedClient();
            return;
        }
        clientDataStore!.setSelectedClientLocationData(
            value.locationId!,
            value.locationName,
            value.areaName
        );
        clientDataStore!.validateSelectedClient();
    };

    const render = () => {
        const hasNoEmptyLocationItem =
            locationSelectorStore.selectedIds.length !== 0;

        const locationList = getLocationList();

        const errorMessage =
            locationList.length === 0 ? t("NoLocationsError") : undefined;

        return (
            <>
                <tr className={styles["info-row-edit-client"]}>
                    {showAddNewTitle && (
                        <td colSpan={3}>
                            <div className={styles["edit-client-warning"]}>
                                <div className={styles["warning-icon"]}>
                                    <WarningSign />
                                </div>
                                <div className={styles["label"]}>
                                    {t("ClientEditWarning")}
                                </div>
                            </div>
                        </td>
                    )}
                </tr>
                <tr
                    className={classNames(styles["edit-client"], {
                        [styles["top-border"]]: !showAddNewTitle,
                        [styles["no-top-border"]]: showAddNewTitle,
                    })}
                    ref={editComponentElement}
                >
                    <td className={styles["first"]}>
                        <div className={styles["column"]}>
                            <ResidentNameField />
                        </div>
                    </td>

                    <td>
                        <div className={styles["column"]}>
                            <LocationSelector
                                noEmptyLocationItem={hasNoEmptyLocationItem}
                                selectedId={
                                    clientDataStore.selectedClient.locationId
                                }
                                label={t("Room").toUpperCase()}
                                locationList={locationList}
                                onValueChange={(value: LocationItem) => {
                                    setSelectedClientLocation(value);
                                }}
                                errorMessage={errorMessage}
                                disabled={locationList.length === 0}
                            />
                        </div>
                    </td>

                    <td className={styles["action-column"]}>
                        <div className={styles["action-buttons-container"]}>
                            <div className={styles["action-buttons"]}>
                                <CancelButton
                                    onClick={() => {
                                        onCancelClick();
                                    }}
                                >
                                    {commonT("Cancel")}
                                </CancelButton>

                                <SaveButton
                                    disabled={
                                        saveLocked || locationList.length === 0
                                    }
                                    onClick={() => {
                                        onSaveClick();
                                    }}
                                >
                                    {commonT("Save")}
                                </SaveButton>
                            </div>
                        </div>
                    </td>
                </tr>
            </>
        );
    };

    return render();
}

export default inject(
    "clientDataStore",
    "locationSelectorStore"
)(observer(EditClient));
